import React, { useContext, useState, useCallback } from 'react';
import { Translator } from 'ggApp/modules/translator';
import { sendCartProductCardClick } from 'snowplow';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { CURRENCIES_ASSOCIATION } from 'ggApp/modules/store/constants';
import { formatPriceByLanguage } from 'ggApp/utils/money';
import { product as productUrlBuilder } from 'ggApp/utils/urlBuilder/frontshop';
import Link from 'ggApp/shared/containers/FrontshopLink';

import UndoModal from 'ggApp/modules/newCheckout/components/UndoModal';
import Bin from 'ggApp/shared/icons/Bin';

import { isUSStore } from 'ggApp/modules/store/utils';
import { Variant } from './Variant';
import { PlanSelector } from './PlanSelector';

import {
    OuterWrap,
    TopWrap,
    BottomWrap,
    MainDetails,
    Image,
    Name,
    CoreAttribute,
    Remove,
    PriceWrap,
    Price,
    Suffix,
    Quantity,
} from './styles';

export interface ItemCardProps {
    cartType?: string;
    item: {
        id: number | string;
        name: string;
        coreAttribute: string;
        quantity: number;
        selectedPlan: number;
        rentalPlans?: Array<string>;
        imageUrl: string;
        totalCents: number;
        variant: {
            colorTitle: string;
            colorCodes: Array<string>;
            product?: {
                slug?: string;
            };
        };
        product?: {
            slug?: string;
        };
    };
    isOrderReview?: boolean;
    closeCheckout: () => {};
}

export const ItemCard = ({
    cartType = 'FLEX',
    item,
    isOrderReview,
    closeCheckout,
}: ItemCardProps): JSX.Element => {
    const { send, cart, activeStore, linkProductsInCartFlag, user, orderNumber } = useContext(
        OrderContext,
    );

    const [undoModal, setUndoModal] = useState(null);

    const createUndoModalRef = (modal): void => {
        setUndoModal(modal);
    };

    const openUndoModal = useCallback(() => {
        if (undoModal) {
            undoModal.openModal();
        }
    }, [undoModal]);

    const { id, name, coreAttribute, quantity, rentalPlans, selectedPlan, imageUrl, totalCents } =
        item || {};

    const removeLastItem = useCallback(() => {
        send('REMOVE_LINE_ITEM', { item });
        send('RESET_CART');
    }, [item, send]);

    const removeFromCart = useCallback(() => {
        if ((cart.items && cart.items.length > 1) || !isOrderReview) {
            send('REMOVE_LINE_ITEM', { item });
        } else {
            openUndoModal();
        }
    }, [item, openUndoModal, isOrderReview, send, cart]);

    const increaseItemQuantity = useCallback(() => {
        const newQuantity = item.quantity + 1;

        send('UPDATE_LINE_ITEM', {
            item: {
                id: isUSStore(activeStore) ? parseInt(item.id as string, 10) : item.id,
                quantity: newQuantity,
            },
        });
    }, [send, item, activeStore]);

    const decreaseItemQuantity = useCallback(() => {
        const newQuantity = item.quantity - 1;
        if (item.quantity > 1) {
            send('UPDATE_LINE_ITEM', {
                item: {
                    id: isUSStore(activeStore) ? parseInt(item.id as string, 10) : item.id,
                    quantity: newQuantity,
                },
            });
        } else {
            removeFromCart();
        }
    }, [item, activeStore, send, removeFromCart]);

    const onImageClick = useCallback(() => {
        sendCartProductCardClick({
            orderID: orderNumber,
            userID: user?.id ?? null,
            productData: {
                id,
                name,
                coreAttribute,
                selectedPlan,
                totalCents,
                slug: item?.product?.slug || item?.variant?.product?.slug,
            },
        });
    }, [orderNumber, user, id, name, coreAttribute, selectedPlan, totalCents, item]);

    return (
        <>
            <OuterWrap>
                <TopWrap>
                    {linkProductsInCartFlag &&
                    (item?.product?.slug || item?.variant?.product?.slug) &&
                    imageUrl ? (
                        <Link
                            target="_self"
                            to={productUrlBuilder(
                                item?.product?.slug ?? item?.variant?.product?.slug,
                            )}
                            onClick={onImageClick}
                        >
                            <Image src={imageUrl} imgUrls={imageUrl} sizes={{ default: '100%' }} />
                        </Link>
                    ) : (
                        imageUrl && (
                            <Image src={imageUrl} imgUrls={imageUrl} sizes={{ default: '100%' }} />
                        )
                    )}

                    <MainDetails>
                        {name ? <Name>{name}</Name> : null}

                        {coreAttribute ? <CoreAttribute>{coreAttribute}</CoreAttribute> : null}
                    </MainDetails>

                    <Variant item={item} isOrderReview={isOrderReview} />

                    {cartType === 'MIX' ? (
                        <Remove onClick={removeFromCart}>
                            <Bin />
                        </Remove>
                    ) : null}
                </TopWrap>

                {cartType === 'FLEX' ? (
                    <BottomWrap>
                        <Quantity
                            quantity={quantity}
                            increaseItemQuantity={increaseItemQuantity}
                            decreaseItemQuantity={decreaseItemQuantity}
                        />

                        <PlanSelector
                            itemId={id}
                            currentPlanLength={selectedPlan}
                            plans={rentalPlans}
                        />

                        <PriceWrap>
                            <Price>
                                {formatPriceByLanguage(
                                    totalCents,
                                    CURRENCIES_ASSOCIATION[activeStore.default_currency],
                                    activeStore.activeLanguage,
                                )}
                            </Price>
                            <Suffix>
                                <Translator tk="PRODUCT_CARD_PLAN_PERIOD_LABEL_SHORT" />
                            </Suffix>
                        </PriceWrap>
                    </BottomWrap>
                ) : null}
            </OuterWrap>

            <UndoModal
                modalRef={createUndoModalRef}
                confirm={removeLastItem}
                closeCheckout={closeCheckout}
            />
        </>
    );
};
